import useDropdown from "@src/modules/shared/components/Dropdown/useDropdown";
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router";

import {
  ImpersonatedUserData,
  setImpersonateUser,
} from "@src/common/helpers/impersonatedUser";
import useUsers from "@src/modules/App/Profile/ViewProfile/hooks/useUsers";
import { ImpersonateDropdownWrapper } from "@src/modules/App/Profile/ViewProfile/ImpersonateDropdown/ImpersonateDropdown.components";
import routesPaths from "@src/modules/Routing/routesPaths";
import Button from "@src/modules/shared/components/Button";
import Dropdown, {
  DropdownOption,
} from "@src/modules/shared/components/Dropdown/Dropdown";
import useImpersonateEventHandler from "@src/common/eventHandlers/useImpersonateEventHandler";

const ImpersonateDropdown: FunctionComponent = () => {
  const { data: users = [], isLoading } = useUsers();
  const dropdown = useDropdown();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const [selectedUser, setSelectedUser] = useState<ImpersonatedUserData>();
  const [impersonating, setImpersonating] = useState<boolean>(false);
  const [impersonatedUser] = useImpersonateEventHandler();
  useEffect(() => {
    if (impersonatedUser !== null) {
      setImpersonating(true);
      setSelectedUser(impersonatedUser);
    }
  }, [impersonatedUser]);

  const options: DropdownOption<string>[] = useMemo(
    () =>
      users.map((u: ImpersonatedUserData) => ({
        value: u.userId,
        name: u.username,
      })),
    [users],
  );

  const handleChange = useCallback(
    (value) => {
      dropdown.toggle();
      const user = users.find((u) => u.userId === value);
      setSelectedUser(user);
    },
    [dropdown, users],
  );

  const toggleImpersonation = () => {
    if (impersonating) {
      setImpersonateUser(null);
      setImpersonating(false);
      setSelectedUser(undefined);
    } else {
      if (selectedUser) {
        setImpersonateUser(selectedUser);
        setImpersonating(true);
      }
    }

    if (location.pathname === routesPaths.HOME) {
      window.location.reload();
    } else {
      history.push(routesPaths.HOME);
    }
  };

  if (users.length === 0) return null;

  return (
    <ImpersonateDropdownWrapper>
      <Dropdown
        style="light"
        options={options}
        isLoading={isLoading}
        value={selectedUser?.userId}
        onChange={handleChange}
        placeholder={t("Select user...")}
        searchable
        fullWidthOption
      />
      <Button onClick={toggleImpersonation} disabled={!selectedUser} fullWidth>
        {t(impersonating ? "Stop impersonation" : "Impersonate")}
      </Button>
    </ImpersonateDropdownWrapper>
  );
};

export default ImpersonateDropdown;
